import { useEffect, useRef, useState } from 'react';
import PortalMark from './PortalMark';
import { APP_ICON } from './PromptSheet';
import pageArt from '../assets/img/install-demo-page.webp';

/**
 * IosInstallDemo — the looping 150 × 260 phone that walks through the iOS 26
 * Safari path: ••• → Share → scroll → Add to Home Screen → Add → home screen.
 * Pure presentation driven by `step` (0–7; 7 is the dark cover that resets
 * every layer before the loop restarts). useIosInstallSteps runs the clock:
 * chained setTimeouts with one duration per step, never setInterval, so a
 * slow frame can't let steps pile up. Design: design_handoff_install_notify_icons §1b.
 */
// tap ••• · tap Share · sheet up · scroll · tap Add to Home Screen · tap Add · home · cover/reset
export const STEP_MS = [1500, 1500, 1000, 1400, 1300, 1600, 1900, 800];
const FIRST_STEP_DELAY_MS = 400;
/** Which list row (01–04) each demo step lights up. */
export const ACTIVE_ROW = [0, 1, 2, 2, 2, 3, 3, 0];
export const STEP_LABELS = ['TAP ••• BELOW', 'TAP SHARE', 'SCROLL DOWN, TAP ADD TO HOME SCREEN', 'TAP ADD'];

export function useIosInstallSteps(running) {
  const [step, setStep] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (!running) { setStep(0); return undefined; }
    let current = 0;
    const advance = () => {
      current = (current + 1) % STEP_MS.length;
      setStep(current);
      timer.current = setTimeout(advance, STEP_MS[current]);
    };
    timer.current = setTimeout(advance, FIRST_STEP_DELAY_MS + STEP_MS[0]);
    return () => clearTimeout(timer.current);
  }, [running]);
  return step;
}

const SYS = '-apple-system, Helvetica, sans-serif';
const TINT = '#D6F7F7';
// Marker position per visible step, in the 142 × 252 frame inside the border.
const TAP = [[122, 232], [112, 203], [74, 246], [74, 205], [74, 205], [127, 15], [127, 15]];
const PRESS_STEPS = new Set([0, 1, 4, 5]);
const SHARE_ROWS = ['Copy', 'Add to Reading List', 'Add Bookmark', 'Add to Favorites', 'Find on Page', 'Add to Home Screen', 'Markup', 'Print'];

const circle = { width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const iconTile = (size, radius) => ({ width: size, height: size, borderRadius: radius, background: `#0E0A22 url(${APP_ICON}) center/cover`, flexShrink: 0 });

export default function IosInstallDemo({ step }) {
  const cover = step === 7;
  const st = cover ? 0 : step;
  const [tapX, tapY] = TAP[st];
  const tapHidden = cover || st === 2 || st === 6;
  const press = !cover && PRESS_STEPS.has(st);

  return (
    <div
      aria-hidden="true"
      style={{ position: 'relative', width: 150, height: 260, boxSizing: 'border-box', border: '4px solid #000', borderRadius: 24, overflow: 'hidden', background: '#1A1A1A', flexShrink: 0 }}
    >
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {/* The page behind it all: a Portal event page, and the app's black bar. */}
        <img src={pageArt} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: .85 }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 22, background: '#000', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
          <PortalMark size={9} still />
        </div>

        {/* Safari's bottom toolbar, in miniature. */}
        <div style={{ position: 'absolute', left: 6, right: 6, bottom: 6, height: 24, display: 'flex', gap: 4 }}>
          <span style={circle}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3"><path d="M15 5l-7 7 7 7" /></svg>
          </span>
          <span style={{ ...circle, flex: 1, width: 'auto', borderRadius: 12, fontFamily: SYS, fontSize: 8, color: '#000' }}>heyportal.app</span>
          <span style={{ ...circle, fontFamily: SYS, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#000', boxShadow: st === 0 ? '0 0 0 2px #00FFFF' : 'none', transition: 'box-shadow .3s' }}>•••</span>
        </div>

        {/* Step 1 — the ••• popover. */}
        <div style={{ position: 'absolute', right: 6, bottom: 36, width: 100, background: '#fff', borderRadius: 12, overflow: 'hidden', fontFamily: SYS, fontSize: 8, color: '#000', transformOrigin: 'bottom right', opacity: st === 1 ? 1 : 0, transform: st === 1 ? 'scale(1)' : 'scale(.6)', transition: 'opacity .25s, transform .25s cubic-bezier(0,0,.2,1)' }}>
          <div style={{ height: 22, display: 'flex', alignItems: 'center', padding: '0 9px', borderBottom: '1px solid #EEE' }}>Add Bookmark</div>
          <div style={{ height: 22, display: 'flex', alignItems: 'center', padding: '0 9px', borderBottom: '1px solid #EEE' }}>Copy</div>
          <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 9px', background: st === 1 ? TINT : '#fff', transition: 'background-color .2s' }}>
            <span>Share</span>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5"><path d="M12 3v12M7 8l5-5 5 5M5 12v9h14v-9" /></svg>
          </div>
        </div>

        {/* Steps 2–4 — the share sheet, scrolling to Add to Home Screen. */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 180, background: '#F2F2F7', borderRadius: '14px 14px 0 0', fontFamily: SYS, color: '#000', transform: st >= 2 && st <= 4 ? 'translateY(0)' : 'translateY(105%)', transition: 'transform .4s cubic-bezier(0,0,.2,1)' }}>
          <div style={{ height: 32, display: 'flex', alignItems: 'center', gap: 6, padding: '0 9px' }}>
            <span style={iconTile(16, 4)} />
            <span style={{ fontSize: 8, fontWeight: 600 }}>Portal</span>
          </div>
          <div style={{ height: 38, display: 'flex', gap: 8, padding: '0 9px', alignItems: 'center' }}>
            {[0, 1, 2, 3].map((i) => <span key={i} style={{ width: 24, height: 24, borderRadius: '50%', background: '#D1D1D6' }} />)}
          </div>
          <div style={{ position: 'absolute', left: 7, right: 7, top: 76, bottom: 0, overflow: 'hidden' }}>
            <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', transform: st >= 3 ? 'translateY(-66px)' : 'translateY(0)', transition: 'transform .7s cubic-bezier(.4,0,.2,1)' }}>
              {SHARE_ROWS.map((label, i) => (
                <div key={label} style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', borderBottom: '1px solid #EEE', fontSize: 8, background: i === 5 && st === 4 ? TINT : '#fff', fontWeight: i === 5 ? 700 : 400, transition: 'background-color .2s' }}>
                  <span>{label}</span>
                  <span style={{ width: 8, height: 8, border: '1.2px solid #000', borderRadius: 2 }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step 5 — the Add to Home Screen dialog. */}
        <div style={{ position: 'absolute', inset: 0, background: '#fff', fontFamily: SYS, color: '#000', transform: st >= 5 ? 'translateX(0)' : 'translateX(105%)', transition: 'transform .4s cubic-bezier(0,0,.2,1)' }}>
          <div style={{ height: 30, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, padding: '0 6px 0 8px', fontSize: 7, whiteSpace: 'nowrap' }}>
            <span style={{ color: '#007AFF' }}>Cancel</span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'center', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>Add to Home</span>
            <span style={{ color: '#007AFF', fontWeight: 700, fontSize: 8, padding: '2px 4px', borderRadius: 4, background: st === 5 ? TINT : 'transparent', transition: 'background-color .2s' }}>Add</span>
          </div>
          <div style={{ margin: '8px 9px 0', display: 'flex', gap: 8, alignItems: 'center', background: '#F2F2F7', borderRadius: 8, padding: 8 }}>
            <span style={iconTile(30, 7)} />
            <span style={{ fontSize: 9, fontWeight: 600 }}>Portal</span>
          </div>
          <div style={{ margin: '8px 9px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F2F2F7', borderRadius: 8, padding: '7px 8px', fontSize: 8 }}>
            <span>Open as Web App</span>
            <span style={{ width: 20, height: 12, borderRadius: 6, background: '#34C759', position: 'relative' }}>
              <span style={{ position: 'absolute', right: 1, top: 1, width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
            </span>
          </div>
        </div>

        {/* Step 6 — the home screen, Portal landing on it. */}
        <div style={{ position: 'absolute', inset: 0, background: '#20242E', display: 'grid', gridTemplateColumns: 'repeat(4, 22px)', gridAutoRows: 22, gap: 10, padding: '24px 12px', alignContent: 'start', opacity: st === 6 ? 1 : 0, transition: 'opacity .35s' }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => <span key={i} style={{ borderRadius: 6, background: '#3A4050' }} />)}
          <span style={{ ...iconTile(22, 6), transform: st === 6 ? 'scale(1)' : 'scale(0)', transition: 'transform .4s cubic-bezier(0,0,.2,1) .2s' }} />
        </div>

        {/* The tap marker: moves between steps, presses on the tapping ones. */}
        <span style={{ position: 'absolute', left: tapX, top: tapY, width: 0, height: 0, opacity: tapHidden ? 0 : 1, transition: 'left .55s cubic-bezier(.4,0,.2,1), top .55s cubic-bezier(.4,0,.2,1), opacity .2s', pointerEvents: 'none' }}>
          <span
            key={`press-${step}`}
            style={{ position: 'absolute', left: '50%', top: '50%', width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', background: 'rgba(0,255,255,.5)', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,.35)', transform: 'translate(-50%,-50%)', animation: press ? `prTap ${STEP_MS[st]}ms cubic-bezier(.4,0,.2,1) both` : 'none' }}
          />
        </span>

        {/* Step 7 — the cover every layer resets behind. */}
        <div style={{ position: 'absolute', inset: 0, background: '#1A1A1A', opacity: cover ? 1 : 0, transition: 'opacity .3s', pointerEvents: 'none' }} />
      </div>
    </div>
  );
}
